
import { useEffect, useState } from "react";
import { 
  fetchUserPriceLocks, 
  PriceLock, 
  cancelPriceLock 
} from "@/lib/priceLockService";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { Lock, Timer, Clock, Ban, AlertCircle } from "lucide-react";
import { useStore } from "@/store/useStore";

export function UserPriceLocks() {
  const [priceLocks, setPriceLocks] = useState<PriceLock[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedLockId, setSelectedLockId] = useState<string | null>(null);
  const { toast } = useToast();
  const { user } = useStore();
  
  useEffect(() => {
    const loadPriceLocks = async () => {
      if (!user) return;
      
      setIsLoading(true);
      try {
        const locks = await fetchUserPriceLocks(user.id);
        setPriceLocks(locks);
      } catch (error) {
        console.error("Error loading price locks:", error);
        toast({
          title: "Errore",
          description: "Impossibile caricare i blocchi prezzo",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    loadPriceLocks();
  }, [user, toast]);
  
  const handleCancelLock = async () => {
    if (!selectedLockId) return;
    
    try {
      const success = await cancelPriceLock(selectedLockId);
      if (success) {
        // Update the local state
        setPriceLocks(locks => 
          locks.map(lock => 
            lock.id === selectedLockId 
              ? { ...lock, status: 'CANCELLED' } 
              : lock
          )
        );
        
        toast({
          title: "Prezzo sbloccato",
          description: "Il blocco prezzo è stato annullato con successo",
        });
      } else {
        throw new Error("Impossibile annullare il blocco prezzo");
      }
    } catch (error) {
      console.error("Error cancelling price lock:", error);
      toast({
        title: "Errore",
        description: "Impossibile annullare il blocco prezzo",
        variant: "destructive",
      });
    } finally {
      setSelectedLockId(null);
    }
  };
  
  const getStatusBadge = (status: PriceLock['status']) => {
    switch (status) {
      case 'ACTIVE':
        return <Badge className="bg-green-500">Attivo</Badge>;
      case 'EXPIRED':
        return <Badge variant="outline" className="text-muted-foreground">Scaduto</Badge>;
      case 'CANCELLED':
        return <Badge variant="destructive">Annullato</Badge>;
      case 'PENDING':
        return <Badge variant="secondary">In attesa</Badge>;
      default:
        return null;
    }
  };
  
  const getTimeRemaining = (expiresAt: string) => {
    const expiry = new Date(expiresAt);
    const now = new Date();
    
    // If already expired
    if (expiry <= now) {
      return "Scaduto";
    }
    
    const diffMs = expiry.getTime() - now.getTime();
    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${diffHrs}h ${diffMins}m`;
  };
  
  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('it-IT', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };
  
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>I tuoi Blocchi Prezzo</CardTitle>
          <CardDescription>Caricamento...</CardDescription>
        </CardHeader>
      </Card>
    );
  }
  
  if (priceLocks.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>I tuoi Blocchi Prezzo</CardTitle>
          <CardDescription>Non hai ancora nessun blocco prezzo attivo</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6 text-muted-foreground">
            <Lock className="h-12 w-12 mx-auto mb-3 opacity-20" />
            <p>Visita un Compro Oro e utilizza la funzione "Blocca Prezzo"</p>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>I tuoi Blocchi Prezzo</CardTitle>
        <CardDescription>Visualizza e gestisci i tuoi blocchi prezzo attivi</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {priceLocks.map((lock) => (
            <div 
              key={lock.id} 
              className="p-4 border rounded-md flex flex-col sm:flex-row sm:items-center gap-4"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h4 className="font-medium">{lock.metal} - {lock.purity}</h4>
                  {getStatusBadge(lock.status)}
                </div>
                
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <Timer className="h-3 w-3" />
                    <span>Peso:</span>
                  </div>
                  <div>{lock.weight}g</div>
                  
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <Lock className="h-3 w-3" />
                    <span>Prezzo:</span>
                  </div>
                  <div>
                    {lock.price.toLocaleString('it-IT', { 
                      style: 'currency', 
                      currency: 'EUR' 
                    })}
                  </div>
                  
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    <span>Creato:</span>
                  </div>
                  <div>{formatDateTime(lock.created_at)}</div>
                </div>
                
                {lock.status === 'ACTIVE' && (
                  <div className="mt-2 text-xs font-medium">
                    Scade tra: {getTimeRemaining(lock.expires_at)}
                  </div>
                )}
              </div>
              
              <div className="flex flex-row sm:flex-col gap-2 self-end sm:self-center">
                {lock.status === 'ACTIVE' && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button 
                        variant="destructive" 
                        size="sm"
                        onClick={() => setSelectedLockId(lock.id)}
                      >
                        <Ban className="h-4 w-4 mr-1" />
                        Annulla
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Conferma annullamento</AlertDialogTitle>
                        <AlertDialogDescription>
                          Sei sicuro di voler annullare questo blocco prezzo? 
                          Questa azione non può essere annullata.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => setSelectedLockId(null)}>
                          Annulla
                        </AlertDialogCancel>
                        <AlertDialogAction onClick={handleCancelLock}>
                          Conferma
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
                <Button 
                  variant="outline" 
                  size="sm"
                  className="text-muted-foreground"
                >
                  Dettagli
                </Button>
              </div>
            </div>
          ))}
        </div>
        
        {priceLocks.some(lock => lock.status === 'ACTIVE') && (
          <div className="mt-4 p-3 bg-yellow-50 rounded-md text-sm flex gap-2 text-yellow-800">
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            <p>
              I blocchi prezzo attivi scadono automaticamente dopo 24 ore. 
              Per usufruire del prezzo bloccato, visita il Compro Oro entro la scadenza.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
